/**
 * Sync between the local SQLite replica and the cloud. One `syncOnce()` round:
 *  0. workspaces and membership: pages of a workspace you are no longer in
 *     leave this device (before pushing, which the server would refuse),
 *  1. upload new attachment files,
 *  2. push changed records (current state + per-field clocks; the server merges),
 *  3. push new content updates (Yjs),
 *  4. pull records and content changed since the last round, merge them locally,
 *     refresh open editors and re-index touched pages; a workspace you just
 *     joined is pulled from the start (its older rows lie behind the cursors).
 * Everything is idempotent: a round interrupted halfway is simply repeated.
 */
import type { Store, SyncRecord } from '@nexus/store';
import { applyToLiveDoc, loadPageJSON, reindexPage } from '@nexus/workspace';
import type { Remote } from './remote';

/** Re-read this many sequence numbers back: rows may commit out of order on the server. */
const OVERLAP = 200;
const BATCH = 500;

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'signed-out';

export interface SyncStatus {
  state: SyncState;
  lastSyncAt: number | null;
  pending: number;
  error: string | null;
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  pages: string[];
}

export class SyncEngine {
  private running: Promise<SyncResult> | null = null;
  private again = false;
  private listeners = new Set<(s: SyncStatus) => void>();
  status: SyncStatus = { state: 'idle', lastSyncAt: null, pending: 0, error: null };

  constructor(
    private readonly store: Store,
    private readonly remote: Remote,
  ) {}

  onStatus(fn: (s: SyncStatus) => void): () => void {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }

  private setStatus(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((fn) => fn(this.status));
  }

  /** Run one round; calls during a round schedule exactly one more. */
  sync(): Promise<SyncResult> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.round()
      .finally(() => {
        this.running = null;
      })
      .then((r) => {
        if (this.again) {
          this.again = false;
          void this.sync();
        }
        return r;
      });
    return this.running;
  }

  private async round(): Promise<SyncResult> {
    const userId = await this.remote.userId();
    if (!userId) {
      this.setStatus({ state: 'signed-out' });
      return { pushed: 0, pulled: 0, pages: [] };
    }
    this.setStatus({ state: 'syncing', error: null });
    try {
      const result = await this.roundFor(userId);
      this.setStatus({
        state: 'idle',
        lastSyncAt: Date.now(),
        pending: await this.store.outboxSize(),
      });
      return result;
    } catch (e) {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      this.setStatus({
        state: offline ? 'offline' : 'error',
        error: e instanceof Error ? e.message : String(e),
        pending: await this.store.outboxSize(),
      });
      throw e;
    }
  }

  private async roundFor(userId: string): Promise<SyncResult> {
    const deviceId = this.store.deviceId;
    let pushed = 0;
    let pulled = 0;
    const touched = new Set<string>();
    const recordsKey = `sync_cursor_records:${userId}`;
    const contentKey = `sync_cursor_content:${userId}`;

    // 0. Workspaces (Deel B): metadata/membership, not CRDT content -- the
    // server is always authoritative, so replace the local copy each round
    // rather than reconciling per field.
    const { workspaces, members } = await this.remote.pullWorkspaces();
    const exists = new Set(workspaces.map((w) => w.id));
    const active = members
      .filter((m) => m.userId === userId && m.status === 'active' && exists.has(m.workspaceId))
      .map((m) => m.workspaceId);
    await this.store.replaceWorkspaces(workspaces, members);
    await this.store.forgetWorkspacesExcept(active);
    // Joined since the last round. A device's first round pulls everything
    // anyway; one that synced before this was tracked catches up on all.
    const knownKey = `sync_workspaces:${userId}`;
    const knownRaw = await this.store.getSetting(knownKey);
    const firstRound = !(await this.store.getSetting(recordsKey));
    const known: string[] = knownRaw ? JSON.parse(knownRaw) : firstRound ? active : [];
    const joined = active.filter((id) => !known.includes(id));

    // 1. attachment files first, so metadata never points at a missing file
    for (;;) {
      const files = await this.store.attachmentsToUpload();
      if (files.length === 0) break;
      for (const f of files) {
        await this.remote.uploadFile(`${userId}/${f.id}`, f.data, f.mime);
        await this.store.markAttachmentUploaded(f.id);
      }
    }

    // 2. records
    for (;;) {
      const { upTo, records } = await this.store.pendingRecords(BATCH);
      if (upTo === 0) break;
      await this.remote.pushRecords(records);
      await this.store.clearOutbox(upTo);
      pushed += records.length;
    }

    // 3. content
    for (;;) {
      const updates = await this.store.unsyncedContent();
      if (updates.length === 0) break;
      await this.remote.pushContent(
        updates.map((u) => ({ pageId: u.pageId, data: u.data, workspaceId: u.workspaceId })),
        deviceId,
      );
      await this.store.markContentSynced(updates.map((u) => u.seq));
      pushed += updates.length;
    }

    // 4a. pull records
    let cursor = Number((await this.store.getSetting(recordsKey)) ?? 0);
    for (;;) {
      const rows = await this.remote.pullRecords(Math.max(0, cursor - OVERLAP), BATCH);
      for (const { record } of rows) {
        if (await this.applyRecord(record, userId)) {
          pulled++;
          if (record.kind === 'prop') touched.add(String(record.fields.pageId));
        }
      }
      const max = rows.reduce((m, r) => Math.max(m, r.seq), cursor);
      if (max <= cursor) break;
      cursor = max;
      await this.store.setSetting(recordsKey, String(cursor));
      if (rows.length < BATCH) break;
    }

    // 4b. pull content
    let contentCursor = Number((await this.store.getSetting(contentKey)) ?? 0);
    for (;;) {
      const rows = await this.remote.pullContent(Math.max(0, contentCursor - OVERLAP), BATCH);
      for (const row of rows) {
        if (row.deviceId === deviceId) continue; // our own updates are already here
        if (await this.store.applyRemoteContent(row.id, row.pageId, row.data)) {
          applyToLiveDoc(row.pageId, row.data);
          touched.add(row.pageId);
          pulled++;
        }
      }
      const max = rows.reduce((m, r) => Math.max(m, r.id), contentCursor);
      if (max <= contentCursor) break;
      contentCursor = max;
      await this.store.setSetting(contentKey, String(contentCursor));
      if (rows.length < BATCH) break;
    }

    // 4c. workspaces joined since the last round, from the start
    for (const workspaceId of joined) {
      for (let after = 0; ;) {
        const rows = await this.remote.pullWorkspaceRecords(workspaceId, after, BATCH);
        for (const { record } of rows) {
          if (await this.applyRecord(record, userId)) {
            pulled++;
            if (record.kind === 'prop') touched.add(String(record.fields.pageId));
          }
        }
        if (rows.length < BATCH) break;
        after = rows.at(-1)!.seq;
      }
      // Our own updates are here already, unless this device forgot the page
      // earlier (decided once per page, before any of its rows come in).
      const hadContent = new Map<string, boolean>();
      for (let after = 0; ;) {
        const rows = await this.remote.pullWorkspaceContent(workspaceId, after, BATCH);
        for (const row of rows) {
          if (!hadContent.has(row.pageId))
            hadContent.set(row.pageId, await this.store.hasContent(row.pageId));
          if (row.deviceId === deviceId && hadContent.get(row.pageId)) continue;
          if (await this.store.applyRemoteContent(row.id, row.pageId, row.data)) {
            applyToLiveDoc(row.pageId, row.data);
            touched.add(row.pageId);
            pulled++;
          }
        }
        if (rows.length < BATCH) break;
        after = rows.at(-1)!.id;
      }
    }
    await this.store.setSetting(knownKey, JSON.stringify(active));

    // Links, tags, tasks and search text of pages that changed elsewhere.
    for (const pageId of touched) {
      if (await this.store.getPage(pageId))
        await reindexPage(this.store, pageId, await loadPageJSON(this.store, pageId));
    }

    return { pushed, pulled, pages: [...touched] };
  }

  private async applyRecord(record: SyncRecord, userId: string): Promise<boolean> {
    if (record.kind !== 'attachment') return this.store.applyRemoteRecord(record);
    if (await this.store.hasAttachment(record.id)) return false;
    const data = await this.remote.downloadFile(`${userId}/${record.id}`);
    await this.store.applyRemoteAttachment(record, data);
    return true;
  }
}
